import React, { useCallback, useState } from "react";
import { Box, Button, Card, Icon, Separator, Text } from "@salestrak/ui";
import type { MapIssue } from "./map-issue";
import { AgeLabel } from "#app/components/age/age-label";
import { UpdateAgeDialog } from "#app/components/age/update-age-dialog";
import { EntityTag, useEntityDetailOptional } from "#app/components/entity";
import { IssueCardAssigneeSelector } from "#app/components/issue-card/issue-card-assignee-selector";
import type { WorkspaceStage, WorkspaceWorkflow } from "#app/lib/data/workspace-data-provider";
import type { Omittable } from "@salestrak/ui";

type InfoboxIssueCardProps = Omittable<{
  issue: MapIssue;
  /** Called when the card is clicked — pass `openIssue` from useIssueView(). */
  onClick: (issue: MapIssue) => void;
  /** Called when the note count badge is clicked. */
  onActivityClick?: (issue: MapIssue) => void;
  /** Show the assignee selector (requires Apollo). Disable inside portals. */
  showAssigneeSelector?: boolean;
  /** Stage lookup map for stage name + color. */
  stageMap?: Map<string, WorkspaceStage>;
  /** Workflow lookup map for workflow name. */
  workflowMap?: Map<string, WorkspaceWorkflow>;
}>;

export const InfoboxIssueCard = React.memo(function InfoboxIssueCard({
  issue,
  onClick,
  onActivityClick,
  showAssigneeSelector = true,
  stageMap,
  workflowMap,
}: InfoboxIssueCardProps) {
  const stage = stageMap?.get(issue.stageId);
  const workflow = workflowMap?.get(issue.workflowId);
  const [ageDialogOpen, setAgeDialogOpen] = useState(false);
  // The card body is the click target, so every inner control stops the click
  // bubbling — otherwise opening the age dialog would also open the issue.
  const handleAgeClick = useCallback((event?: React.MouseEvent<HTMLElement>) => {
    event?.stopPropagation();
    setAgeDialogOpen(true);
  }, []);
  // Optional: the infobox can render inside map portals where the provider is absent.
  const entityDetail = useEntityDetailOptional();
  const handleViewProfile = useCallback(
    (userId: string) => entityDetail?.openEntity("user", userId),
    [entityDetail],
  );

  return (
    <>
      <Box cursor="pointer" onClick={() => onClick(issue)}>
        <Card radius="sm" className="overflow-hidden shadow-md">
          <Box paddingBottom="sm" paddingTop="sm" paddingX="sm">
            {/* Header */}
            <Box paddingX="xs">
              <Box align="start" direction="row" gap="xs">
                <Box fill="flex">
                  <Text size="sm" weight="semibold">
                    {issue.name}
                  </Text>
                </Box>
              </Box>
              {issue.address?.wholeAddress && (
                <Text size="xs" variant="muted" weight="medium">
                  {issue.address.wholeAddress}
                </Text>
              )}

              {/* Stage / Workflow line */}
              {(stage || workflow) && (
                <Box align="center" direction="row" gap="xs" paddingTop="xs">
                  {stage && (
                    <EntityTag
                      entity={{
                        kind: "stage",
                        id: stage.stageId,
                        stage,
                        workflowId: issue.workflowId,
                      }}
                      interactive={false}
                      size="xs"
                      weight="semibold"
                    />
                  )}
                  {stage && workflow && (
                    <Text size="xs" variant="muted">
                      ·
                    </Text>
                  )}
                  {workflow && (
                    <EntityTag
                      display="label"
                      entity={{ kind: "workflow", id: workflow.workflowId, workflow }}
                      interactive={false}
                      size="xs"
                    />
                  )}
                </Box>
              )}
            </Box>

            <Box paddingTop="sm">
              <Separator />
            </Box>

            {/* Footer */}
            <Box align="center" direction="row" justify="between" paddingTop="sm">
              <Box align="center" direction="row" gap="xs">
                <AgeLabel
                  age={issue.age}
                  buttonSize="xs"
                  onClick={handleAgeClick}
                  size="xs"
                />
                {issue.noteCount > 0 && (
                  <Button
                    onClick={(event) => {
                      event.stopPropagation();
                      if (onActivityClick) onActivityClick(issue);
                      else onClick(issue);
                    }}
                    size="xs"
                    variant="ghost"
                  >
                    <Icon color="muted" name="message-square" size="md" />
                    <Text size="xs" variant="muted" weight="semibold">
                      {issue.noteCount}
                    </Text>
                  </Button>
                )}
              </Box>

              {/* Assignee selector (requires Apollo — hidden inside portals) */}
              {showAssigneeSelector && (
                <Box shrink>
                  <IssueCardAssigneeSelector
                    assignees={issue.assignees ?? []}
                    issueId={issue.issueId}
                    onViewProfile={handleViewProfile}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Card>
      </Box>
      <UpdateAgeDialog
        age={issue.age}
        issueId={issue.issueId}
        onOpenChange={setAgeDialogOpen}
        open={ageDialogOpen}
        stageId={issue.stageId}
      />
    </>
  );
});
